import React, {useRef, useEffect, useMemo, useCallback} from 'react';
import {
    Avatar,
    Box,
    Divider,
    List,
    ListItem,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {DT_FORMATS} from 'utils';
import {getRandomColor} from '../utils';
import {ChatMessageMarkdown} from './ChatMessageMarkdown';
import {useChatContext} from '../context';

type ChatMessagesProps = {
    scrollToBottomRef: React.MutableRefObject<() => void>;
    onScrollPositionChange?: (isAtBottom: boolean) => void;
};

export const ChatMessages = ({scrollToBottomRef, onScrollPositionChange}: ChatMessagesProps) => {
    const {
        currentChatMessages: {read: readList, unread: unreadList},
        loadMoreHistory,
        hasMoreHistory,
        isLoadingMoreHistory,
        selectedChat,
        currentUser,
        readMessages,
    } = useChatContext();

    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const userId = currentUser?.username;

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const initialScrollDoneRef = useRef<string | null>(null);
    const targetIdRef = useRef<number | null>(null);

    // трекаем мы внизу? и последнее увиденное сообщение
    const BOTTOM_EPS = 40;
    const isAtBottomRef = useRef(true);
    const lastSeenIdRef = useRef<number | null>(null);

    useEffect(() => {
        initialScrollDoneRef.current = null;

        messageRefs.current.clear();
        isAtBottomRef.current = true;
        lastSeenIdRef.current = null;
    }, [selectedChat?.id]);

    const allMessages = useMemo(() => {
        // склеиваем списки и убираем дубли по id
        const map = new Map<number | string, NonNullable<typeof readList>[number]>();
        [...(readList ?? []), ...(unreadList ?? [])].forEach((m) => {
            map.set(m.id as number, m);
        });
        const unique = Array.from(map.values());
        //  сортировка сперва по  id
        // на крайний случай  по sentAt
        return unique.sort((a, b) => {
            const aId = typeof a.id === 'number' ? a.id : Number.NEGATIVE_INFINITY;
            const bId = typeof b.id === 'number' ? b.id : Number.NEGATIVE_INFINITY;
            if (aId !== bId) return aId - bId;

            //одинаковые id — сравним по времени
            return new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime();
        });
    }, [readList, unreadList]);

    // индекс/ид первого непрочитанного и последнего сообщения
    const firstUnreadIndex = useMemo(() => {
        if (!unreadList?.length) return -1;
        const ids = new Set(unreadList.map((m) => m.id));
        return allMessages.findIndex((m) => ids.has(m.id));
    }, [allMessages, unreadList]);

    const firstUnreadId = useMemo(
        () => (firstUnreadIndex >= 0 ? (allMessages[firstUnreadIndex]?.id ?? null) : null),
        [firstUnreadIndex, allMessages],
    );

    const lastMessageId = useMemo(
        () => (allMessages.length ? allMessages[allMessages.length - 1].id : null),
        [allMessages],
    );

    const handleScrollToBottom = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.scrollTo({top: container.scrollHeight, behavior: 'smooth'});

        isAtBottomRef.current = true;
        onScrollPositionChange?.(true);
    }, [onScrollPositionChange]);

    useEffect(() => {
        scrollToBottomRef.current = handleScrollToBottom;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleScrollToBottom]);

    //скролл к первому непрочитанному или последнему сообщению
    useEffect(() => {
        targetIdRef.current = firstUnreadId ?? lastMessageId ?? null;
        initialScrollDoneRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChat?.id, firstUnreadId]);

    // подгрузка истории
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const onScroll = async () => {
            const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
            const newIsAtBottom = distance <= BOTTOM_EPS;

            if (isAtBottomRef.current !== newIsAtBottom) {
                isAtBottomRef.current = newIsAtBottom;
                onScrollPositionChange?.(newIsAtBottom);
            }

            if (el.scrollTop === 0 && hasMoreHistory && !isLoadingMoreHistory) {
                const prevH = el.scrollHeight;
                await loadMoreHistory();
                requestAnimationFrame(() => {
                    el.scrollTop = el.scrollHeight - prevH;
                });
            }
        };

        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, [loadMoreHistory, hasMoreHistory, isLoadingMoreHistory, onScrollPositionChange]);

    // автоскролл вниз после отправки своего сообщения
    useEffect(() => {
        if (!allMessages.length) return;
        const container = scrollContainerRef.current;
        if (!container) return;

        const last = allMessages[allMessages.length - 1];
        const currentId = last.id as number;

        if (lastSeenIdRef.current === currentId) return;

        if (last.fromUserId === userId) {
            requestAnimationFrame(() => {
                container.scrollTo({top: container.scrollHeight, behavior: 'auto'});
                if (!isAtBottomRef.current) {
                    isAtBottomRef.current = true;
                    onScrollPositionChange?.(true);
                }
            });
            lastSeenIdRef.current = currentId;
        }
    }, [allMessages, userId, onScrollPositionChange]);

    // входящие — вниз только если мы у нижней кромки и появился новый последний id
    useEffect(() => {
        if (!allMessages.length) return;
        const container = scrollContainerRef.current;
        if (!container) return;

        const last = allMessages[allMessages.length - 1];
        const currentId = last.id as number;

        if (last.fromUserId === userId) return;

        // если последний id не изменился  выходим
        if (lastSeenIdRef.current === currentId) return;

        if (isAtBottomRef.current) {
            requestAnimationFrame(() => {
                container.scrollTo({top: container.scrollHeight, behavior: 'auto'});
                isAtBottomRef.current = true;
            });
        }

        lastSeenIdRef.current = currentId;
    }, [allMessages, userId]);

    // пометка непрочитанных как прочитанных
    useEffect(() => {
        if (!unreadList?.length) return;
        const container = scrollContainerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const ids: number[] = [];
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        const id = Number(e.target.getAttribute('data-msg-id'));
                        if (id) {
                            ids.push(id);
                            observer.unobserve(e.target);
                        }
                    }
                });
                if (ids.length) readMessages(ids);
            },
            {root: container, threshold: 0.5},
        );

        unreadList.forEach((m) => {
            const el = messageRefs.current.get(m.id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [unreadList, readMessages]);

    return (
        <Box
            ref={scrollContainerRef}
            sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 2,
                background: dark ? 'var(--background-paper-elevation-11, #313131)' : '#fff',
            }}
        >
            <List>
                {allMessages.map((message, index) => {
                    const isUserMessage = message.fromUserId === userId;
                    const isFirstUnread = index === firstUnreadIndex && firstUnreadIndex !== -1;
                    const hasUnread = (unreadList ?? []).length > 0;

                    return (
                        <React.Fragment key={message.id}>
                            {isFirstUnread && hasUnread && !isUserMessage && (
                                <Divider
                                    textAlign='center'
                                    sx={{my: 2, color: 'text.secondary'}}
                                >
                                    Новые сообщения
                                </Divider>
                            )}

                            <ListItem
                                component='div'
                                data-msg-id={message.id}
                                data-first-unread={isFirstUnread}
                                ref={(el) => {
                                    if (!el) {
                                        messageRefs.current.delete(message.id);
                                        return;
                                    }
                                    messageRefs.current.set(message.id, el);
                                    // первичный автоскролл только при откртыии чата
                                    if (
                                        initialScrollDoneRef.current !== selectedChat?.id &&
                                        targetIdRef.current != null &&
                                        message.id === targetIdRef.current
                                    ) {
                                        // ждём layout кадр, потом скроллим
                                        requestAnimationFrame(() => {
                                            el.scrollIntoView({
                                                block: firstUnreadId ? 'center' : 'end',
                                                behavior: 'auto',
                                            });
                                            initialScrollDoneRef.current = selectedChat!.id;
                                            // если целевой элемент был последний — мы у нижней кромки
                                            const container = scrollContainerRef.current!;
                                            const distance =
                                                container.scrollHeight -
                                                container.scrollTop -
                                                container.clientHeight;
                                            const atBottom = distance <= BOTTOM_EPS;
                                            isAtBottomRef.current = atBottom;
                                            onScrollPositionChange?.(atBottom);
                                        });
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
                                    py: 0.5,
                                }}
                            >
                                <Paper
                                    elevation={2}
                                    sx={{
                                        maxWidth: '85%',
                                        p: 1,
                                        bgcolor: isUserMessage
                                            ? 'rgba(3,155,229,0.35)'
                                            : theme.palette.background.paper,
                                        borderRadius: isUserMessage
                                            ? '12px 0 12px 12px'
                                            : '0 12px 12px 12px',
                                    }}
                                >
                                    {!isUserMessage && selectedChat?.type === 'GROUP' && (
                                        <Stack
                                            direction='row'
                                            gap={1}
                                            mb={0.5}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    backgroundColor: getRandomColor(
                                                        message.fromUserId,
                                                    ),
                                                }}
                                            >
                                                {message.fromUserId[0]}
                                            </Avatar>
                                            <Typography variant='caption'>
                                                {message.fromUserId}
                                            </Typography>
                                        </Stack>
                                    )}

                                    <ChatMessageMarkdown text={message.content!} />

                                    <Typography
                                        variant='caption'
                                        sx={{
                                            mt: 1,
                                            color: 'text.secondary',
                                            textAlign: 'right',
                                            backgroundColor: 'transparent !important',
                                        }}
                                    >
                                        {dayjs(message.sentAt).format(DT_FORMATS.default)}
                                    </Typography>
                                </Paper>
                            </ListItem>
                        </React.Fragment>
                    );
                })}
            </List>
        </Box>
    );
};
